import { useMutation } from '@apollo/client';
import type { Policy, CreatePolicyInput } from '@communities/content-management/domain';
import { CREATE_COMMUNITY_POLICY, GET_COMMUNITY_POLICIES } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface CreateCommunityPolicyResponse {
  createPolicy: Policy;
}

export function useCreateCommunityPolicy(communityId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [createPolicyMutation, { loading }] = useMutation<CreateCommunityPolicyResponse>(CREATE_COMMUNITY_POLICY, {
    refetchQueries: communityId ? [{ query: GET_COMMUNITY_POLICIES, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const createPolicy = async (communityId: string, input: CreatePolicyInput): Promise<boolean> => {
    try {
      track('CreateCommunityPolicyInitiated', {
        communityId,
        policyName: input.name,
      });

      setErrors(null);
      await createPolicyMutation({
        variables: {
          communityId,
          input,
        },
      });

      track('CreateCommunityPolicySucceeded', {
        communityId,
        policyName: input.name,
      });

      return true;
    } catch (error) {
      track('CreateCommunityPolicyFailed', {
        communityId,
        policyName: input.name,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    createPolicy,
    loading,
    errors,
    resetErrors,
  };
}

export default useCreateCommunityPolicy;
